import { Api } from "@/plugins/axios"
import { errorHandler } from "@/plugins/errorHandler"
import { getField, updateField } from "vuex-map-fields"

let filters = {
    search: '',
    has_triggers: false,
    has_active_leads: false
}

export const state = () => ({
    filters: filters,
    page: 1,
    showFilters: false,
    automation: {},
    steps: [],
    triggers: [],
    selectedNode: null,
    leadStatuses: [],
    leadTags: [],
    automationLoading: false,
    statusesLoading: false,
    tagsLoading: false,
    creatingTag: false,
    emailTemplateEditorOpen: false
})

export const getters = {
    getField,

    triggers: (state) => {
        return state.triggers
    },

    emailSteps: (state) => state.steps
        .filter(s => s.type === 'sendEmail')
        .map(s => ({
            id: s.id,
            label: s.title || s.additional_settings?.email_subject || `Email ${s.id}`
        })),

    smsSteps: (state) => state.steps
        .filter(s => s.type === 'sendMessage')
        .map(s => ({
            id: s.id,
            label: s.title || (s.additional_settings?.message_text?.substring(0, 30) + '...') || `Text ${s.id}`
        })),

    stepOptions: (state) => state.steps.map(s => ({
        id: s.id,
        label: s.title || s.type
    })),

    stepById: (state) => (id) => state.steps.find(s => s.id === id || s.id === parseInt(id))
}

export const mutations = {
    updateField,

    setAutomation(state, automation) {
        state.automation = automation || {}
        state.steps = automation?.automation_steps || []
        state.triggers = automation?.automation_triggers || []
    },

    setSteps(state, steps) {
        state.steps = steps || []
    },

    addStep(state, step) {
        state.steps.push(step)
    },

    updateStep(state, step) {
        const index = state.steps.findIndex(s => s.id === step.id)
        if (index > -1) {
            state.steps.splice(index, 1, { ...state.steps[index], ...step })
        }
    },

    removeStep(state, id) {
        const index = state.steps.findIndex(s => s.id === id || s.id === parseInt(id))
        if (index > -1) state.steps.splice(index, 1)
    },

    setSelectedNode(state, node) {
        state.selectedNode = node
    },

    setLeadStatuses(state, statuses) {
        state.leadStatuses = statuses || []
    },

    setLeadTags(state, tags) {
        state.leadTags = tags || []
    },

    addLeadTag(state, tag) {
        state.leadTags.push(tag)
    },

    setAutomationLoading(state, value) {
        state.automationLoading = value
    },

    setStatusesLoading(state, value) {
        state.statusesLoading = value
    },

    setTagsLoading(state, value) {
        state.tagsLoading = value
    },

    setCreatingTag(state, value) {
        state.creatingTag = value
    },

    setEmailTemplateEditorOpen(state, value) {
        state.emailTemplateEditorOpen = !!value
    },

    reset(state) {
        state.automation = {}
        state.steps = []
        state.selectedNode = null
        state.emailTemplateEditorOpen = false
    },

    resetFilter(state) {
        state.filters = {
            search: '',
            has_triggers: false,
            has_active_leads: false
        }
        state.page = 1
    }
}

export const actions = {
    async loadAutomation({ commit }, id) {
        if (!id) return
        commit('setAutomationLoading', true)
        const { data: { automation, errors, message }, status } = await Api.get(`/api/v1/automations/view/${id}`).catch(e => e)
        commit('setAutomationLoading', false)
        if (errorHandler(status, message, errors)) return
        commit('setAutomation', automation)
        return automation
    },

    async saveAutomation({ commit, state }) {
        if (!state.automation?.id) return
        commit('setAutomationLoading', true)
        // Only send the automation's own settings. Steps and triggers are saved by their own editors, so the
        // copies hanging off state.automation are stale and would overwrite the saved attributes and ordering.
        const { data: { automation, errors, message }, status } = await Api.post(
            `/api/v1/automations/edit/${state.automation.id}`,
            {
                name: state.automation.name,
                description: state.automation.description,
                enabled: state.automation.enabled,
                global_triggers: state.automation.global_triggers
            }
        ).catch(e => e)
        commit('setAutomationLoading', false)
        if (errorHandler(status, message, errors)) return
        commit('setAutomation', automation)
        return automation
    },

    async addStep({ commit, state }, step) {
        const { data: { automationStep, success }, status } = await Api.post(
            '/api/v1/automation-steps/add',
            {
                automation_id: state.automation.id,
                uuid: step.id,
                action: step.type
            }
        ).catch(e => e)
        commit('addStep', automationStep)
        return automationStep
    },

    async addTrigger({ commit, state }, trigger){
        const { data: { automationTrigger, success }, status } = await Api.post(
            '/api/v1/automation-triggers/add',
            {
                automation_id: state.automation.id,
                uuid: trigger.id,
                automation_trigger: trigger.type
            }
        ).catch(e => e)
        return automationTrigger
    },

    async saveOrder({ commit, state }, stepOrder){
        Api.post('/api/v1/automation-steps/save-order/' + state.automation.id, { order : stepOrder })
    },

    async removeStep({ commit, state }, step){
        const { data : { success } } = await Api.post('/api/v1/automation-steps/delete', {
            uuid: step.id,
            automation_id: state.automation.id
        })
    },

    async removeTrigger({ commit, state }, trigger){
        const { data : { success } } = await Api.post('/api/v1/automation-triggers/delete', {
            uuid: trigger.id,
            automation_id: state.automation.id
        })
    },

    async updateStep({ commit }, step) {
        const { data: { step: saved, errors, message }, status } = await Api.post(
            `/api/v1/automation-steps/${step.id}/edit`,
            step
        ).catch(e => e)
        if (errorHandler(status, message, errors)) return
        commit('updateStep', saved)
        return saved
    },

    async deleteStep({ commit }, id) {
        const { data: { errors, message }, status } = await Api.delete(
            `/api/v1/automation-steps/${id}/delete`
        ).catch(e => e)
        if (errorHandler(status, message, errors)) return
        commit('removeStep', id)
    },

    async loadStatuses({ commit, state }) {
        if (state.leadStatuses.length > 0) return state.leadStatuses
        commit('setStatusesLoading', true)
        const { data, status } = await Api.get('/api/v1/statuses').catch(e => e)
        commit('setStatusesLoading', false)
        if (status !== 200) return
        const statuses = (data.statuses || []).map(s => ({ id: s.id, label: s.name }))
        commit('setLeadStatuses', statuses)
        return statuses
    },

    async loadTags({ commit, state }) {
        if (state.leadTags.length > 0) return state.leadTags
        commit('setTagsLoading', true)
        const { data, status } = await Api.get('/api/v1/user-tags/search-tags').catch(e => e)
        commit('setTagsLoading', false)
        if (status !== 200) return
        const tags = (data.tags || []).map(t => ({ id: t.id, label: t.text }))
        commit('setLeadTags', tags)
        return tags
    },

    async createTag({ commit }, text) {
        commit('setCreatingTag', true)
        const { data, status } = await Api.post('/api/v1/user-tags/add', { text }).catch(e => e)
        commit('setCreatingTag', false)
        if (status !== 200 || !data?.tag) return { error: data?.errors?.text || data?.message || 'Failed to create tag' }
        const tag = { id: data.tag.id, label: data.tag.text }
        commit('addLeadTag', tag)
        return { tag }
    },

    selectNode({ commit }, node) {
        commit('setSelectedNode', node)
    },

    reset({ commit }) {
        commit('reset')
    }
}
